import * as React from 'react'

import type { Form } from 'factorial-form'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Text from 'modules/core/components/Inputs/Text'
import i18n from 'modules/core/lib/i18n'

import { UserSelect } from 'modules/automations/components/blocks/UserSelect'
import type { BranchOperation } from 'modules/automations/lib/Store/Branch'
import { ConditionTypes } from 'modules/automations/lib/enums'
import { TriggerAttributes } from 'modules/automations/lib/triggerAttributes'
import { ITriggerCondition } from 'modules/automations/store/models/Trigger'
import ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type CustomFieldsCollection from 'modules/custom_resources/store/collections/customFields/fields'

import CustomFieldSelect from './CustomFieldSelect'
import FolderSelect from './FolderSelect'
import { RegularSelect } from './RegularSelect'

interface Props {
  disabled: boolean
  form: Form
  operation: BranchOperation
  loadingCustomFields: boolean
  customFieldsCollection: CustomFieldsCollection
  condition?: ITriggerCondition
  extensionFeatureHasConditions: boolean
}

export default function ValueSelect({
  disabled,
  form,
  operation,
  loadingCustomFields,
  customFieldsCollection,
  condition,
  extensionFeatureHasConditions,
}: Props) {
  const valueSelect = () => {
    if (condition?.key === 'folder_id') {
      return <FolderSelect field={form.get('value')} />
    }

    switch (condition?.type) {
      case ConditionTypes.SingleChoice:
        return (
          <RegularSelect
            disabled={disabled}
            form={form}
            operation={operation}
            condition={condition}
          />
        )
      case ConditionTypes.Boolean:
      case ConditionTypes.Identifier:
        return <RegularSelect disabled={disabled} form={form} operation={operation} />
      case ConditionTypes.User:
        return <UserSelect disabled={disabled} field={form.get('value')} />
      case ConditionTypes.Date:
        return (
          <DayPicker label={i18n.t('automations.actions.branch.value')} field={form.get('value')} />
        )
      default:
        return null
    }
  }

  const oldValueSelect = () => {
    const attribute = operation.attribute

    switch (attribute) {
      case TriggerAttributes.employee_id:
      case TriggerAttributes.manager_id:
      case TriggerAttributes.timeoff_manager_id:
      case TriggerAttributes.assignee_id:
      case TriggerAttributes.author_id:
        return (
          <UserSelect
            useEmployee={TriggerAttributes.employee_id === operation.attribute}
            disabled={disabled}
            field={form.get('value')}
          />
        )

      case TriggerAttributes.birthday:
      case TriggerAttributes.startsOn:
      case TriggerAttributes.endsOn:
      case TriggerAttributes.trialPeriodEndsOn:
        return (
          <DayPicker label={i18n.t('automations.actions.branch.value')} field={form.get('value')} />
        )

      case TriggerAttributes.jobTitle:
      case TriggerAttributes.salaryAmount:
      case TriggerAttributes.workingHours:
        return <Text label={i18n.t('automations.actions.branch.value')} field={form.get('value')} />

      case TriggerAttributes.spainProfessionalCategories:
      case TriggerAttributes.educationLevel:
      case TriggerAttributes.cotizationGroup:
        return <RegularSelect disabled={disabled} form={form} operation={operation} />
      case ContractVersion.CustomContractAttribute(attribute):
        return <Text label={i18n.t('automations.actions.branch.value')} field={form.get('value')} />

      case TriggerAttributes.custom_fields:
      case TriggerAttributes.custom_resources:
        return (
          <CustomFieldSelect
            disabled={disabled}
            form={form}
            operation={operation}
            loadingCustomFields={loadingCustomFields}
            customFieldsCollection={customFieldsCollection}
          />
        )

      default:
        return <RegularSelect disabled={disabled} form={form} operation={operation} />
    }
  }

  return extensionFeatureHasConditions ? valueSelect() : oldValueSelect()
}
